
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useEffectOnActive } from "keepalive-for-react";
import { Button } from "@/components/ui/button";
import { MarketplaceItem as MarketplaceItemType } from "@/types";
import { AlertCircle, RefreshCw, Archive } from "lucide-react";
import {
  buildMarketplaceSections,
  isListingArchived,
  MarketplaceSection,
  sortNewestFirst,
} from "@/lib/marketplaceListing";
import { fetchCountries } from "@/services/countryService";
import { useLanguage } from "@/context/LanguageContext";
import MarketplaceItem from "@/components/marketplace/MarketplaceItem";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { fetchMarketplaceItems, synchronizeMarketplaceWithCollection } from "@/services/marketplaceService";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/context/ThemeContext";
import { BanknoteFilterMarketplace, MARKETPLACE_PREFERENCES_KEY } from "@/components/filter/BanknoteFilterMarketplace";
import { useBanknoteFilter } from "@/hooks/use-banknote-filter";
import { FilterOption } from "@/components/filter/BaseBanknoteFilter";
import SEOHead from "@/components/seo/SEOHead";
import Canonical from "@/components/seo/Canonical";
import { SEO_CONFIG } from "@/config/seoConfig";
import { useTranslation } from "react-i18next";

const SULTAN_DISPLAY_ORDER: Record<string, number> = {
  AbdulMecid: 1,
  AbdulAziz: 2,
  Murad: 3,
  AbdulHamid: 4,
  "M.Resad": 5,
  "M.Vahdeddin": 6
};

const Marketplace = () => {

  const { user } = useAuth();
  const { theme } = useTheme();
  const { toast } = useToast();
  const { t, i18n: i18nInstance } = useTranslation(['marketplace', 'pages']);
  const [marketplaceItems, setMarketplaceItems] = useState<MarketplaceItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<FilterOption[]>([]);
  const [availableTypes, setAvailableTypes] = useState<FilterOption[]>([]);
  const [availableCountries, setAvailableCountries] = useState<FilterOption[]>([]);
  const [countryOrder, setCountryOrder] = useState<Array<{ id: string; name: string; name_ar?: string; name_tr?: string; display_order: number }>>([]);
  // Country sections whose beyond-two-weeks auctions are expanded (spec §8.3).
  const [expandedFar, setExpandedFar] = useState<Set<string>>(new Set());
  // Quick views (client remark 8.1 §5): sections per country in catalog order,
  // or one flat list of everything, newest published first.
  const [viewMode, setViewMode] = useState<'countries' | 'newest'>('countries');
  const { currentLanguage } = useLanguage();



  // Memoize the fallback function to prevent infinite re-renders
  const tWithFallback = useMemo(() => {
    return (key: string, fallback: string) => {
      const translation = t(key);
      return translation === key ? fallback : translation;
    };
  }, [t]);

  // Remove unnecessary translation reloading - handled by i18next automatically

  const loadMarketplaceItems = useCallback(async (showToast = false) => {
    setLoading(true);
    setError(null);
    try {

      if (user?.role === 'Admin') {
        await synchronizeMarketplaceWithCollection();
      }

      const items = await fetchMarketplaceItems(user?.id);


      if (items.length === 0) {
        if (showToast) {
          toast({
            title: tWithFallback('status.noItems', 'No Items Found'),
            description: tWithFallback('status.noItemsDescription', 'There are currently no items available in the marketplace'),
            variant: "default"
          });
        }
      }

      // Extract available categories, types, and countries from fetched items
      const categoryMap = new Map<string, FilterOption>();
      const typeMap = new Map<string, FilterOption>();
      const countryMap = new Map<string, FilterOption>();

      items.forEach(item => {
        const { series, type, country } = item.collectionItem?.banknote || {};

        // Process category (series)
        if (series) {
          const id = series.toLowerCase().replace(/\s+/g, '-');
          categoryMap.set(id, { id, name: series });
        }

        // Process type
        if (type) {
          const id = type.toLowerCase().replace(/\s+/g, '-');
          typeMap.set(id, { id, name: type });
        }

        // Process country
        if (country) {
          const id = country.toLowerCase().replace(/\s+/g, '-');
          countryMap.set(id, { id, name: country });
        }
      });

      // Update available filters
      setAvailableCategories(Array.from(categoryMap.values()));
      setAvailableTypes(Array.from(typeMap.values()));
      setAvailableCountries(Array.from(countryMap.values()));

      setMarketplaceItems(items);

    } catch (err) {
      console.error("Error loading marketplace items:", err);
      setError(tWithFallback('status.errorDescription', 'Failed to load marketplace items. Please try again later.'));
      toast({
        title: tWithFallback('status.error', 'Error loading marketplace items'),
        description: tWithFallback('status.errorDescription', 'Failed to load marketplace items. Please try again later.'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [toast, user?.role, user?.id, t]);

  useEffect(() => {

    loadMarketplaceItems();
  }, [loadMarketplaceItems]);

  // /marketplace is kept alive (see config/keepAlive.ts), so navigating away and
  // back does NOT remount this page and the mount effect above never re-runs.
  // Without this, a listing promoted elsewhere — e.g. an admin approving its URL,
  // which flips it from PendingUrl to Available — would keep showing its stale
  // status until a full browser reload. skipMount avoids double-fetching on the
  // first render.
  useEffectOnActive(() => {
    loadMarketplaceItems();
  }, [loadMarketplaceItems], true);

  useEffect(() => {
    fetchCountries().then((data) =>
      setCountryOrder(
        data.map((c) => ({
          id: c.id,
          name: c.name,
          name_ar: c.name_ar ?? undefined,
          name_tr: c.name_tr ?? undefined,
          display_order: c.display_order ?? 999,
        }))
      )
    );
  }, []);

  // The owner's Draft / PendingUrl listings are pinned above everything (spec §8.3).
  const ownerPendingItems = useMemo(
    () => marketplaceItems.filter((i) => i.status !== 'Available'),
    [marketplaceItems]
  );
  const publishedItems = useMemo(
    () => marketplaceItems.filter((i) => i.status === 'Available'),
    [marketplaceItems]
  );

  // Transform marketplace items to have the banknote property at the top level
  // This allows useBanknoteFilter to work correctly, while preserving collectionItem for price sorting
  const marketplaceItemsForFilter = useMemo(() => {
    return publishedItems.map(item => ({
      ...item,
      banknote: item.collectionItem?.banknote,
      collectionItem: item.collectionItem // Preserve collectionItem for price/date sorting
    }));
  }, [publishedItems]);

  const {
    filteredItems,
    filters,
    setFilters,
    groupedItems
  } = useBanknoteFilter({
    items: marketplaceItemsForFilter,
    initialFilters: {
      sort: ["newest"] // Default to "Newest Listed" when no user preferences are loaded
    }
  });

  // Spec §11 ordering: active items sectioned by country (Ottoman first),
  // archive rendered the same way further down the scroll.
  const activeItems = useMemo(
    () => (filteredItems ?? []).filter((i: any) => !isListingArchived(i)),
    [filteredItems]
  );
  const archivedItems = useMemo(
    () => (filteredItems ?? []).filter((i: any) => isListingArchived(i)),
    [filteredItems]
  );
  const activeSections = useMemo(
    () => buildMarketplaceSections(activeItems as any[], countryOrder),
    [activeItems, countryOrder]
  );
  const archivedSections = useMemo(
    () => buildMarketplaceSections(archivedItems as any[], countryOrder),
    [archivedItems, countryOrder]
  );

  const localizedCountryName = useCallback((section: MarketplaceSection) => {
    const country = countryOrder.find((c) => c.name === section.countryName);
    if (!country) return section.countryName;
    if (currentLanguage === 'ar') return country.name_ar || country.name;
    if (currentLanguage === 'tr') return country.name_tr || country.name;
    return country.name;
  }, [countryOrder, currentLanguage]);

  const toggleFar = useCallback((countryId: string) => {
    setExpandedFar((prev) => {
      const next = new Set(prev);
      if (next.has(countryId)) next.delete(countryId);
      else next.add(countryId);
      return next;
    });
  }, []);

  

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadMarketplaceItems(true);
  };

  const handleFilterChange = useCallback((newFilters: any) => {
    setFilters(newFilters);
  }, [setFilters]);

  const handleAllCountries = useCallback(() => {
    setViewMode('countries');
    setFilters({ ...filters, countries: [] });
    // The filter bar restores saved countries on the next visit; clear them too
    // so "All Countries" sticks.
    if (user?.id) {
      const key = `${MARKETPLACE_PREFERENCES_KEY}-${user.id}`;
      try {
        const saved = JSON.parse(localStorage.getItem(key) || 'null');
        if (saved) localStorage.setItem(key, JSON.stringify({ ...saved, countries: [] }));
      } catch {
        // Unreadable prefs are simply left alone.
      }
    }
  }, [filters, setFilters, user?.id]);

  const loadingSection = useMemo(() => {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Spinner size="lg" />
        <p className="dark:text-ottoman-300 text-ottoman-600">{tWithFallback('status.loading', 'Loading marketplace items...')}</p>
      </div>
    );
  }, [t]);

  const errorSection = useMemo(() => {
    if (!error) return null;

    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle><span>{tWithFallback('status.error', 'Error loading marketplace items')}</span></AlertTitle>
        <AlertDescription>
          {error}
          <Button
            variant="outline"
            className="mt-4 dark:border-ottoman-700 border-ottoman-300 dark:text-ottoman-200 text-ottoman-800"
            onClick={handleRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {tWithFallback('status.tryAgain', 'Try Again')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }, [error, handleRefresh, t]);

  const emptySection = useMemo(() => {
    const hasActiveFilters = filters && (filters.categories?.length > 0 || filters.types?.length > 0 || filters.search || filters.countries?.length > 0);
    return (
      <Card className="text-center py-20 dark:bg-dark-600/50 bg-white/90 dark:border-ottoman-900/30 border-ottoman-200/70">
        <h3 className="text-2xl font-serif font-semibold dark:text-ottoman-200 text-ottoman-800 mb-2">
          <span>{tWithFallback('status.noItems', 'No Items Found')}</span>
        </h3>
        <p className="dark:text-ottoman-400 text-ottoman-600 mb-6">
          {hasActiveFilters
            ? tWithFallback('status.noItemsFiltered', 'No items match your current filters. Try adjusting your criteria.')
            : tWithFallback('status.noItemsDescription', 'There are currently no items available in the marketplace')}
        </p>
        <div className="space-x-4">
          <Button
            className="ottoman-button"
            onClick={handleRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {tWithFallback('actions.refresh', 'Refresh')}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={() => setFilters({ categories: [], types: [], search: "", sort: ["newest"], countries: [] })}
            >
              {tWithFallback('filters.clearFilters', 'Clear Filters')}
            </Button>
          )}
        </div>
      </Card>
    );
  }, [handleRefresh, filters, setFilters, t]);

  const renderGrid = useCallback((items: MarketplaceItemType[], keyPrefix: string) => (
    <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
      {items.map((item, index) => (
        <div
          key={`${keyPrefix}-${item.id ?? index}`}
          className="animate-fade-in"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <MarketplaceItem item={item} />
        </div>
      ))}
    </div>
  ), []);

  const renderSection = useCallback((section: MarketplaceSection, keyPrefix: string) => {
    const farOpen = expandedFar.has(`${keyPrefix}-${section.countryId}`);
    return (
      <div key={`${keyPrefix}-${section.countryId}`} className="mb-8">
        <h2 className="text-2xl font-serif font-bold dark:text-ottoman-200 text-ottoman-800 border-b border-ottoman-200/70 dark:border-ottoman-900/40 pb-1 mb-2">
          <span>{localizedCountryName(section)}</span>
        </h2>
        {section.nearAuctions.length > 0 && renderGrid(section.nearAuctions as MarketplaceItemType[], `${keyPrefix}-near`)}
        {section.farAuctions.length > 0 && (
          <div className="pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleFar(`${keyPrefix}-${section.countryId}`)}
            >
              {farOpen
                ? t('listing.hideUpcomingAuctions')
                : t('listing.showUpcomingAuctions', { count: section.farAuctions.length })}
            </Button>
            {farOpen && renderGrid(section.farAuctions as MarketplaceItemType[], `${keyPrefix}-far`)}
          </div>
        )}
        {section.buyNow.length > 0 && renderGrid(section.buyNow as MarketplaceItemType[], `${keyPrefix}-buy`)}
      </div>
    );
  }, [expandedFar, localizedCountryName, renderGrid, t, toggleFar]);

  const marketplaceItemsSection = useMemo(() => {
    if (activeItems.length === 0 && archivedItems.length === 0 && ownerPendingItems.length === 0) {
      return null;
    }
    return (
      <div>
        {/* Owner's drafts & PendingUrl items, pinned on top (spec §8.3). */}
        {ownerPendingItems.length > 0 && (
          <div className="mb-8 rounded-lg border border-dashed border-ottoman-400/60 p-3">
            <h2 className="text-xl font-serif font-bold mb-1">
              <span>{t('listing.yourListings')}</span>
            </h2>
            {renderGrid(ownerPendingItems, 'pending')}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pb-2">
          <Button
            size="sm"
            variant="outline"
            aria-pressed={viewMode === 'countries'}
            onClick={handleAllCountries}
            className={cn(
              "transition-[transform,background-color,color] duration-150 active:scale-[0.97]",
              viewMode === 'countries' && "bg-ottoman-600 text-white border-ottoman-600 hover:bg-ottoman-700"
            )}
          >
            {t('listing.allCountries')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            aria-pressed={viewMode === 'newest'}
            onClick={() => setViewMode('newest')}
            className={cn(
              "transition-[transform,background-color,color] duration-150 active:scale-[0.97]",
              viewMode === 'newest' && "bg-ottoman-600 text-white border-ottoman-600 hover:bg-ottoman-700"
            )}
          >
            {t('listing.newestFirst')}
          </Button>
        </div>

        {viewMode === 'countries'
          ? activeSections.map((s) => renderSection(s, 'active'))
          : activeItems.length > 0 && <div className="mb-8">{renderGrid(sortNewestFirst(activeItems as any[]) as MarketplaceItemType[], 'newest')}</div>}

        {/* Archive, further down the same scroll (spec §10). */}
        {archivedItems.length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-serif font-bold flex items-center gap-2 dark:text-ottoman-300 text-ottoman-700 mb-3">
              <Archive className="w-5 h-5" />
              <span>{t('listing.archiveHeading')}</span>
            </h2>
            <div className="opacity-80">
              {viewMode === 'countries'
                ? archivedSections.map((s) => renderSection(s, 'archive'))
                : renderGrid(sortNewestFirst(archivedItems as any[]) as MarketplaceItemType[], 'archive-newest')}
            </div>
          </div>
        )}
      </div>
    );
  }, [activeItems, archivedItems, ownerPendingItems, activeSections, archivedSections, renderGrid, renderSection, t, viewMode, handleAllCountries]);

  const contentSection = useMemo(() => {
    if (loading) {
      return loadingSection;
    } else if (error) {
      return errorSection;
    } else if (!marketplaceItemsSection) {
      return emptySection;
    } else {
      return marketplaceItemsSection;
    }
  }, [loading, error, loadingSection, errorSection, emptySection, marketplaceItemsSection]);

  return (
    <div className="min-h-screen animate-fade-in">
      <Canonical />
      <SEOHead
        title={SEO_CONFIG.pages.marketplace.title}
        description={SEO_CONFIG.pages.marketplace.description}
        keywords={SEO_CONFIG.pages.marketplace.keywords}
        type="website"
      />
      <section className={`${theme === 'light' ? 'bg-ottoman-100' : 'bg-dark-600'} py-12 relative overflow-hidden`}>
        <div className="absolute inset-0 -z-10">
          <div className={`absolute inset-y-0 right-1/2 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-30deg] ${theme === 'light'
              ? 'bg-ottoman-500/10 shadow-ottoman-300/20 ring-ottoman-400/10'
              : 'bg-dark-500/40 shadow-ottoman-900/20 ring-ottoman-900/10'
            } shadow-xl ring-1 ring-inset`} aria-hidden="true" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <h1 className={`text-3xl md:text-4xl font-serif font-bold text-center ${theme === 'light' ? 'text-ottoman-900' : 'text-parchment-500'} fade-bottom`}>
            <span>{tWithFallback('title', 'Marketplace')}</span>
          </h1>
          <p className={`mt-4 text-center ${theme === 'light' ? 'text-ottoman-700' : 'text-ottoman-300'} max-w-2xl mx-auto fade-bottom`}>
            {tWithFallback('subtitle', 'Browse and purchase Ottoman banknotes from fellow collectors')}
          </p>
        </div>
      </section>

      <div className="bg-card border rounded-lg mt-10 p-1 sm:p-6 mb-6 sm:w-[95%] w-auto mx-auto">
        <section className="py-1">
          <div className="container mx-auto px-4">
              <BanknoteFilterMarketplace
                onFilterChange={handleFilterChange}
                currentFilters={filters}
                isLoading={loading}
                availableCategories={availableCategories}
                availableTypes={availableTypes}
                availableCountries={availableCountries}
              />

            {contentSection}
            
          </div>
        </section>
      </div>
    </div>
  );
};

export default Marketplace;
